import React, { ReactNode } from 'react';

import cx from 'classnames';

import { Button, ConfigProvider, Modal, Typography } from '@/widgets';
import themeConfig from '@/theme/themeConfig';
import { TOUR_SKIPPED, TOUR_STARTED } from '@/constants';
import { sendClickEvent } from '@/utils/AnalyticsUtils';
import { TEST_IDS } from '@/constants/testConstants';

import styles from './TourModal.module.scss';
interface ITourModalProps {
	title?: ReactNode;
	description: ReactNode;
	banner?: string;
	onSkip?: () => void;
	takeTour?: () => void;
	isOpen: boolean;
}
const {
	COACH_MARK_TOUR_MODAL_SKIP_BUTTON,
	COACH_MARK_TOUR_MODAL_BANNER_CONTENT,
	COACH_MARK_TOUR_MODAL_TAKE_A_TOUR_BUTTON,
	COACH_MARK_TOUR_MODAL_BANNER_IMAGE,
} = TEST_IDS;
/**
 *
 * @description UI component for coachmark landing modal
 *
 * @author  Rohit Niraj Singh <@rohit.singh3>
 *
 * @returns {React.ReactElement}
 */

export default function TourModal({
	title,
	description,
	banner,
	takeTour,
	onSkip,
	isOpen,
}: Readonly<ITourModalProps>) {
	const initTour = () => {
		sendClickEvent({ name: TOUR_STARTED, value: window.location.pathname });
		takeTour?.();
	};

	const skipTour = () => {
		sendClickEvent({ name: TOUR_SKIPPED });
		onSkip?.();
	};

	const renderBanner = () => {
		return (
			<div
				className={styles.bannerContainer}
				data-id={COACH_MARK_TOUR_MODAL_BANNER_IMAGE}
			>
				<img className={styles.banner} src={banner} alt="tour modal banner" />
			</div>
		);
	};

	const renderContent = () => {
		return (
			<div
				className={styles.content}
				data-id={COACH_MARK_TOUR_MODAL_BANNER_CONTENT}
			>
				{title ? (
					<Typography weight="semibold" as="h3">
						{title}
					</Typography>
				) : null}
				<section>
					<Typography className={styles.description} weight="regular" as="body">
						{description}
					</Typography>
				</section>
			</div>
		);
	};

	const renderFooter = () => {
		return (
			<div className={styles.footer}>
				<Button
					size="large"
					onClick={skipTour}
					variant="text"
					className={cx(styles.cancelBtn)}
				>
					<Typography
						as="body"
						weight="semibold"
						data-id={`${COACH_MARK_TOUR_MODAL_SKIP_BUTTON}`}
					>
						Skip
					</Typography>
				</Button>
				<Button onClick={initTour} variant="primary" size="large">
					<Typography
						as="body"
						weight="semibold"
						data-id={`${COACH_MARK_TOUR_MODAL_TAKE_A_TOUR_BUTTON}`}
					>
						Take a Tour
					</Typography>
				</Button>
			</div>
		);
	};

	return (
		<ConfigProvider
			theme={{
				token: {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					colorPrimary: themeConfig.primary,
				},
			}}
		>
			<Modal
				width="auto"
				className={styles.modalWrapper}
				footer={null}
				open={isOpen}
				destroyOnClose
				closable={false}
			>
				{renderBanner()}
				<div className={styles.body}>
					{renderContent()}
					{renderFooter()}
				</div>
			</Modal>
		</ConfigProvider>
	);
}
